/**
 * Roadmap View Page
 *
 * Displays a roadmap with a Gantt chart visualization.
 * Shows themes as rows with items as timeline bars.
 */

import { useRoadmapFull } from '@medsuite/api-client';
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { GanttChart } from './components/GanttChart';
import { RoadmapItemEditor } from './components/RoadmapItemEditor';

import type { RoadmapItemWithInvestment } from '@medsuite/models';

// Month names for display
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Zoom levels: year (full year), quarter (4 quarters), month (6 months)
type ZoomLevel = 'year' | 'quarter' | 'month';

// Helper to get current quarter (1-4) from current date
const getCurrentQuarter = () => Math.floor(new Date().getMonth() / 3) + 1;

/**
 * RoadmapViewPage Component
 *
 * Features:
 * - Back navigation button
 * - Roadmap title and company info
 * - Year/Quarter/Month navigation (prev/next)
 * - Zoom levels: Year, Quarter (4Q rolling), Month (6M rolling)
 * - Gantt chart with yearly, quarterly or monthly columns
 * - Clickable items to open editor modal
 */
export function RoadmapViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roadmapId = parseInt(id || '0', 10);

  const { data: roadmap, isLoading, error } = useRoadmapFull(roadmapId);

  // Zoom level state - default to quarter view
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('quarter');

  // Year and quarter navigation state
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [startQuarter, setStartQuarter] = useState(() => getCurrentQuarter()); // Start on current quarter

  // Month navigation state (for month view)
  const [startMonth, setStartMonth] = useState(() => new Date().getMonth()); // Start on current month

  // Item editor modal state
  const [editingItem, setEditingItem] = useState<RoadmapItemWithInvestment | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Calculate year range from items
  const yearRange = useMemo(() => {
    if (!roadmap?.themes) {
      return { min: selectedYear, max: selectedYear };
    }

    let minYear = selectedYear;
    let maxYear = selectedYear;

    roadmap.themes.forEach((theme) => {
      theme.items?.forEach((item) => {
        const startYear = new Date(item.start_date).getFullYear();
        const endYear = new Date(item.end_date).getFullYear();
        minYear = Math.min(minYear, startYear);
        maxYear = Math.max(maxYear, endYear);
      });
    });

    return { min: minYear, max: maxYear };
  }, [roadmap, selectedYear]);

  // Zoom in (year -> quarter -> month)
  const handleZoomIn = () => {
    if (zoomLevel === 'year') {
      // Switching to quarter view - keep on current quarter or Q1
      setZoomLevel('quarter');
    } else if (zoomLevel === 'quarter') {
      // Switching to month view - set startMonth based on current quarter
      setStartMonth((startQuarter - 1) * 3);
      setZoomLevel('month');
    }
    // Already at month level, can't zoom in further
  };

  // Zoom out (month -> quarter -> year)
  const handleZoomOut = () => {
    if (zoomLevel === 'month') {
      // Switching to quarter view - set startQuarter based on current month
      setStartQuarter(Math.floor(startMonth / 3) + 1);
      setZoomLevel('quarter');
    } else if (zoomLevel === 'quarter') {
      // Switching to year view - show full year starting Q1
      setStartQuarter(1);
      setZoomLevel('year');
    }
    // Already at year level, can't zoom out further
  };

  // Navigation handlers - behavior depends on zoom level
  // << button: moves by larger increment (year in year/quarter view, quarter in month view)
  const handlePrevLarge = () => {
    if (zoomLevel === 'year' || zoomLevel === 'quarter') {
      setSelectedYear((y) => Math.max(y - 1, yearRange.min - 1));
      if (zoomLevel === 'quarter') {
        setStartQuarter(1);
      }
    } else {
      // In month view, << moves by quarter (3 months)
      for (let i = 0; i < 3; i++) {
        if (startMonth === 0) {
          setSelectedYear((y) => Math.max(y - 1, yearRange.min - 1));
          setStartMonth(11 - (2 - i));
          break;
        } else {
          setStartMonth((m) => Math.max(0, m - 1));
        }
      }
      // Simplified: just move back 3 months
      const newMonth = startMonth - 3;
      if (newMonth < 0) {
        setSelectedYear((y) => Math.max(y - 1, yearRange.min - 1));
        setStartMonth(12 + newMonth);
      } else {
        setStartMonth(newMonth);
      }
    }
  };

  // >> button: moves by larger increment
  const handleNextLarge = () => {
    if (zoomLevel === 'year' || zoomLevel === 'quarter') {
      setSelectedYear((y) => Math.min(y + 1, yearRange.max + 1));
      if (zoomLevel === 'quarter') {
        setStartQuarter(1);
      }
    } else {
      // In month view, >> moves by quarter (3 months)
      const newMonth = startMonth + 3;
      if (newMonth > 11) {
        setSelectedYear((y) => Math.min(y + 1, yearRange.max + 1));
        setStartMonth(newMonth - 12);
      } else {
        setStartMonth(newMonth);
      }
    }
  };

  // < button: moves by smaller increment (quarter in quarter view, month in month view, year in year view)
  const handlePrevSmall = () => {
    if (zoomLevel === 'year') {
      // In year view, < also moves by year
      setSelectedYear((y) => Math.max(y - 1, yearRange.min - 1));
    } else if (zoomLevel === 'quarter') {
      if (startQuarter === 1) {
        setSelectedYear((y) => Math.max(y - 1, yearRange.min - 1));
        setStartQuarter(4);
      } else {
        setStartQuarter((q) => q - 1);
      }
    } else {
      // In month view, < moves by month
      if (startMonth === 0) {
        setSelectedYear((y) => Math.max(y - 1, yearRange.min - 1));
        setStartMonth(11);
      } else {
        setStartMonth((m) => m - 1);
      }
    }
  };

  // > button: moves by smaller increment
  const handleNextSmall = () => {
    if (zoomLevel === 'year') {
      // In year view, > also moves by year
      setSelectedYear((y) => Math.min(y + 1, yearRange.max + 1));
    } else if (zoomLevel === 'quarter') {
      if (startQuarter === 4) {
        setSelectedYear((y) => Math.min(y + 1, yearRange.max + 1));
        setStartQuarter(1);
      } else {
        setStartQuarter((q) => q + 1);
      }
    } else {
      // In month view, > moves by month
      if (startMonth === 11) {
        setSelectedYear((y) => Math.min(y + 1, yearRange.max + 1));
        setStartMonth(0);
      } else {
        setStartMonth((m) => m + 1);
      }
    }
  };

  // Calculate the display range
  const getRangeDisplay = () => {
    if (zoomLevel === 'year') {
      return `${selectedYear}`;
    } else if (zoomLevel === 'quarter') {
      const endQuarter = ((startQuarter - 1 + 3) % 4) + 1;
      const endYear = startQuarter > 1 ? selectedYear + 1 : selectedYear;

      if (startQuarter === 1) {
        return `Q1 - Q4 ${selectedYear}`;
      } else {
        return `Q${startQuarter} ${selectedYear} - Q${endQuarter} ${endYear}`;
      }
    } else {
      // Month view - show 6 months
      const endMonth = (startMonth + 5) % 12;
      const endYear = startMonth + 5 >= 12 ? selectedYear + 1 : selectedYear;

      if (startMonth === 0 && endMonth === 5) {
        return `${MONTH_NAMES[0]} - ${MONTH_NAMES[5]} ${selectedYear}`;
      } else {
        return `${MONTH_NAMES[startMonth]} ${selectedYear} - ${MONTH_NAMES[endMonth]} ${endYear}`;
      }
    }
  };

  // Get navigation button tooltips based on zoom level
  const getNavTooltips = () => {
    if (zoomLevel === 'year') {
      return {
        prevLarge: 'Previous Year',
        prevSmall: 'Previous Year',
        nextSmall: 'Next Year',
        nextLarge: 'Next Year',
      };
    } else if (zoomLevel === 'quarter') {
      return {
        prevLarge: 'Previous Year',
        prevSmall: 'Previous Quarter',
        nextSmall: 'Next Quarter',
        nextLarge: 'Next Year',
      };
    } else {
      return {
        prevLarge: 'Previous Quarter',
        prevSmall: 'Previous Month',
        nextSmall: 'Next Month',
        nextLarge: 'Next Quarter',
      };
    }
  };

  const navTooltips = getNavTooltips();

  const handleItemClick = (item: RoadmapItemWithInvestment) => {
    setEditingItem(item);
    setIsCreating(false);
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setIsCreating(true);
  };

  const handleCloseEditor = () => {
    setEditingItem(null);
    setIsCreating(false);
  };

  if (!roadmapId || roadmapId <= 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Invalid Roadmap ID
          </h1>
          <button
            onClick={() => navigate('/roadmap')}
            className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
          >
            Return to Roadmaps
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-full px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Back Button */}
              <button
                onClick={() => navigate('/roadmap')}
                className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <svg
                  className="mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
                Back
              </button>

              {/* Title */}
              {roadmap && (
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {roadmap.roadmap_title}
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {roadmap.company_name}
                    {roadmap.roadmap_context && ` - ${roadmap.roadmap_context}`}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-4">
              {/* Zoom Controls */}
              <div className="flex items-center rounded-lg bg-white dark:bg-gray-800 shadow">
                {/* Zoom Out Button */}
                <button
                  onClick={handleZoomOut}
                  disabled={zoomLevel === 'year'}
                  className="p-1.5 rounded-l-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Zoom out"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6"
                    />
                  </svg>
                </button>
                {/* Current Zoom Level */}
                <span className="px-2 text-xs font-medium text-gray-600 dark:text-gray-400 border-x border-gray-200 dark:border-gray-700">
                  {zoomLevel === 'year' ? 'Year' : zoomLevel === 'quarter' ? 'Qtr' : 'Month'}
                </span>
                {/* Zoom In Button */}
                <button
                  onClick={handleZoomIn}
                  disabled={zoomLevel === 'month'}
                  className="p-1.5 rounded-r-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Zoom in"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6"
                    />
                  </svg>
                </button>
              </div>

              {/* Combined Navigation: << < [range] > >> */}
              <div className="flex items-center rounded-lg bg-white dark:bg-gray-800 px-2 py-1.5 shadow">
                {/* Previous Large (<<) */}
                <button
                  onClick={handlePrevLarge}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  title={navTooltips.prevLarge}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5"
                    />
                  </svg>
                </button>
                {/* Previous Small (<) */}
                <button
                  onClick={handlePrevSmall}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  title={navTooltips.prevSmall}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 19.5L8.25 12l7.5-7.5"
                    />
                  </svg>
                </button>
                {/* Date Range Display */}
                <span className="px-3 text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[160px] text-center">
                  {getRangeDisplay()}
                </span>
                {/* Next Small (>) */}
                <button
                  onClick={handleNextSmall}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  title={navTooltips.nextSmall}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 4.5l7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </button>
                {/* Next Large (>>) */}
                <button
                  onClick={handleNextLarge}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  title={navTooltips.nextLarge}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </button>
              </div>

              {/* Add Item Button */}
              {roadmap && roadmap.themes && roadmap.themes.length > 0 && (
                <button
                  onClick={handleAddItem}
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
                >
                  <svg
                    className="-ml-0.5 mr-1.5 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                  Add Item
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {error ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg
                className="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Error loading roadmap
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
          </div>
        ) : isLoading ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500"></div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading roadmap...</p>
          </div>
        ) : !roadmap ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Roadmap not found
            </h3>
            <button
              onClick={() => navigate('/roadmap')}
              className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
            >
              Return to Roadmaps
            </button>
          </div>
        ) : !roadmap.themes || roadmap.themes.length === 0 ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
              />
            </svg>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              No themes yet
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Create themes in the Admin section to add items to the roadmap
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/admin')}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Go to Admin
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-white dark:bg-gray-800 shadow overflow-hidden">
            <GanttChart
              themes={roadmap.themes}
              year={selectedYear}
              startQuarter={startQuarter}
              zoomLevel={zoomLevel}
              startMonth={startMonth}
              onItemClick={handleItemClick}
            />
          </div>
        )}

        {/* Item Editor Modal */}
        {(editingItem || isCreating) && roadmap && (
          <RoadmapItemEditor
            item={editingItem}
            roadmapId={roadmapId}
            themes={roadmap.themes || []}
            onClose={handleCloseEditor}
          />
        )}
      </div>
    </div>
  );
}

export default RoadmapViewPage;
